import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { Badge, Button, Modal, TableData } from '@/components';
import {
  showErrorToast,
  showSuccessToast,
} from '@/components/toast/globalToast';
import {
  ClientExportTemplateMapping,
  ExportTemplateSummary,
  exportTemplatesAPI,
} from '@/services/api/exportTemplates';
import { getProviderNamesByIds } from '@/services/api/providers';
import { Column } from '@/types/common';

import MappingFormModal from './components/MappingFormModal';

// Built-in layouts that are ALWAYS global — they are the default for every
// client and cannot be owned by one. The assign/clear actions are hidden for
// them (they never have a client_export_templates row).
const GLOBAL_TEMPLATE_KEYS = new Set(['default', 'compact']);

const fmtDate = (iso: string | null) =>
  iso
    ? new Date(iso).toLocaleString('id-ID', {
        dateStyle: 'medium',
        timeStyle: 'short',
      })
    : '—';

/** One row = one registered template + its owning client (null = all clients). */
interface TemplateRow {
  templateKey: string;
  clientId: number | null;
  updatedBy: string | null;
  updatedAt: string | null;
}

const ExportTemplatesPage: React.FC = () => {
  const [templates, setTemplates] = useState<ExportTemplateSummary[]>([]);
  const [mappings, setMappings] = useState<ClientExportTemplateMapping[]>([]);
  const [clientNames, setClientNames] = useState<Record<number, string>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [editing, setEditing] = useState<TemplateRow | null>(null);
  const [clearing, setClearing] = useState<TemplateRow | null>(null);
  const [clearBusy, setClearBusy] = useState(false);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    // Independent fetches — a mappings failure must NOT hide the templates
    // (and vice versa). allSettled keeps whatever loaded.
    const [tplsRes, mapsRes] = await Promise.allSettled([
      exportTemplatesAPI.listTemplates(),
      exportTemplatesAPI.listMappings(),
    ]);
    if (tplsRes.status === 'fulfilled') setTemplates(tplsRes.value);
    if (mapsRes.status === 'fulfilled') setMappings(mapsRes.value);
    if (tplsRes.status === 'rejected' && mapsRes.status === 'rejected') {
      setError(
        tplsRes.reason instanceof Error
          ? tplsRes.reason.message
          : 'Failed to load export templates.'
      );
    } else if (tplsRes.status === 'rejected') {
      setError('Could not load the registered templates — try Refresh.');
    } else if (mapsRes.status === 'rejected') {
      setError('Could not load client assignments — try Refresh.');
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  // Resolve the owning clients' NAMES (the table shows names, not ids). Only the
  // handful of ids actually mapped are looked up; unresolved ones fall back to
  // the id in the cell.
  useEffect(() => {
    const ids = Array.from(
      new Set(
        mappings
          .map((m) => m.clientId)
          .filter((id): id is number => id != null)
      )
    );
    if (ids.length === 0) {
      setClientNames({});
      return;
    }
    let cancelled = false;
    getProviderNamesByIds(ids)
      .then((names) => {
        if (!cancelled) setClientNames(names);
      })
      .catch(() => {
        /* leave names empty — cells fall back to the id */
      });
    return () => {
      cancelled = true;
    };
  }, [mappings]);

  // Every registered template is a row; join in its client owner (if any).
  const rows = useMemo<TemplateRow[]>(() => {
    const byTemplate = new Map(mappings.map((m) => [m.templateKey, m]));
    return templates.map((t) => {
      const m = byTemplate.get(t.key);
      return {
        templateKey: t.key,
        clientId: m?.clientId ?? null,
        updatedBy: m?.updatedBy ?? null,
        updatedAt: m?.updatedAt ?? null,
      };
    });
  }, [templates, mappings]);

  const onSaved = () => {
    setEditing(null);
    fetchAll();
  };

  const handleClear = async () => {
    if (!clearing) return;
    setClearBusy(true);
    try {
      await exportTemplatesAPI.deleteMapping(clearing.templateKey);
      showSuccessToast(
        'Now global',
        `"${clearing.templateKey}" is available to all clients.`
      );
      setClearing(null);
      fetchAll();
    } catch (e) {
      showErrorToast(
        'Failed to clear',
        e instanceof Error ? e.message : 'Could not clear the assignment.'
      );
    } finally {
      setClearBusy(false);
    }
  };

  const columns: Column<TemplateRow>[] = [
    {
      key: 'templateKey',
      title: 'Template',
      render: (_v, r) => <Badge tone="primary">{r.templateKey}</Badge>,
    },
    {
      key: 'clientId',
      title: 'Client',
      render: (_v, r) => {
        if (GLOBAL_TEMPLATE_KEYS.has(r.templateKey)) {
          return (
            <span className="inline-flex items-center gap-1.5 text-sm text-gray-500">
              <i className="ri-lock-2-line text-[13px] text-gray-400" />
              All clients
            </span>
          );
        }
        if (r.clientId == null) {
          return <span className="text-sm text-gray-400">All clients</span>;
        }
        return (
          <span className="text-sm text-gray-800">
            {clientNames[r.clientId] ?? `#${r.clientId}`}
          </span>
        );
      },
    },
    {
      key: 'updatedBy',
      title: 'Updated by',
      render: (_v, r) => (
        <span className="text-sm text-gray-500">{r.updatedBy || '—'}</span>
      ),
    },
    {
      key: 'updatedAt',
      title: 'Updated at',
      render: (_v, r) => (
        <span className="text-sm text-gray-500">{fmtDate(r.updatedAt)}</span>
      ),
    },
    {
      key: 'actions',
      title: '',
      align: 'right',
      render: (_v, r) => {
        // default / compact are permanently global — no client can own them.
        if (GLOBAL_TEMPLATE_KEYS.has(r.templateKey)) {
          return (
            <span className="text-[11px] italic text-gray-400">
              Global (locked)
            </span>
          );
        }
        return (
          <div className="inline-flex items-center justify-end gap-1">
            <Button
              variant="outline-accent"
              size="xs"
              onClick={() => setEditing(r)}
            >
              {r.clientId != null ? 'Change' : 'Assign client'}
            </Button>
            {r.clientId != null && (
              <Button variant="white" size="xs" onClick={() => setClearing(r)}>
                Make global
              </Button>
            )}
          </div>
        );
      },
    },
  ];

  return (
    <div className="space-y-5 p-6">
      <header className="flex flex-col gap-3 border-b border-gray-200 pb-5 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <h1 className="text-[28px] font-extrabold leading-none tracking-tight text-gray-900">
            Export Templates
          </h1>
          <p className="mt-2 max-w-2xl text-sm leading-relaxed text-gray-500">
            Every registered export layout and the client that owns it.{' '}
            <span className="font-mono">default</span> /{' '}
            <span className="font-mono">compact</span> are global (all clients);
            a client can own several (e.g. Patimban owns export + import).
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="white" onClick={fetchAll}>
            Refresh
          </Button>
          {/* Phase 2: upload a client's sheet, AI generates the template. */}
          <span title="Coming soon — upload a client sheet, AI builds the template">
            <Button variant="white" disabled>
              + New template (upload)
            </Button>
          </span>
        </div>
      </header>

      <div className="rounded-gsm border border-gray-200 bg-white p-4">
        {error ? (
          <div className="flex flex-col items-center gap-3 py-10 text-center">
            <p className="text-sm text-red-500">{error}</p>
            <Button variant="white" onClick={fetchAll}>
              Retry
            </Button>
          </div>
        ) : (
          <TableData<TemplateRow>
            data={rows}
            columns={columns}
            loading={loading}
            size="small"
            emptyState={
              <p className="text-sm text-gray-500">
                No registered templates.
              </p>
            }
          />
        )}
      </div>

      <MappingFormModal
        isOpen={!!editing}
        templateKey={editing?.templateKey ?? ''}
        currentClientId={editing?.clientId ?? null}
        onClose={() => setEditing(null)}
        onSaved={onSaved}
      />

      <Modal
        isOpen={!!clearing}
        onClose={() => setClearing(null)}
        icon="ri-global-line"
        iconTone="warning"
        title="Make template global?"
        subtitle={
          clearing
            ? `"${clearing.templateKey}" will be available to all clients.`
            : undefined
        }
        width="sm"
        closeOnBackdropClick={!clearBusy}
        closeOnEscape={!clearBusy}
        footer={
          <div className="flex items-center justify-end gap-2">
            <Button
              variant="white"
              onClick={() => setClearing(null)}
              disabled={clearBusy}
            >
              Cancel
            </Button>
            <Button onClick={handleClear} disabled={clearBusy}>
              {clearBusy ? 'Clearing…' : 'Make global'}
            </Button>
          </div>
        }
      >
        <p className="text-sm text-gray-600">
          This removes the client owner. Any client can then pick this template
          in Custom export.
        </p>
      </Modal>
    </div>
  );
};

export default ExportTemplatesPage;
